/*
 * Opens the Shopify Inbox chat on demand.
 *
 * The default floating launcher is hidden (see the Inbox container rules in
 * custom.css). Shopify has shipped that container under different tags over
 * time (#shopify-chat, and now <shopify-agent> with its launcher in a shadow
 * DOM), so both the hiding and this script match several selectors. Any
 * [data-open-inbox] element reveals + opens the chat; the custom FAB
 * (.after-chat-fab) is hidden while the chat is open (.chat-open on <html>) and
 * returns when it closes. Reveal state lives on <html> so it no longer depends
 * on the Inbox container's exact tag/id.
 */
(function () {
  var closeObserver = null;

  function getHost() {
    // Shopify keeps renaming the Inbox launcher container. The newest build uses
    // the TAG <shopify-chat> (not the old id #shopify-chat), so match the tag too.
    return document.querySelector(
      'shopify-chat, shopify-agent, inbox-online-store-chat, #ShopifyChat, #shopify-chat'
    );
  }

  function getToggle(host) {
    if (!host || !host.shadowRoot) {
      return document.querySelector('.activator, .chat-toggle');
    }
    var sr = host.shadowRoot;
    return (
      sr.querySelector('.activator') ||
      sr.querySelector('.chat-toggle') ||
      sr.querySelector('button') ||
      document.querySelector('.activator, .chat-toggle')
    );
  }

  // The current Inbox widget (<shopify-agent>) marks the open panel with an
  // `open` attribute; older builds used is-open="true". Support both.
  function isChatOpen(host) {
    return (
      !!host &&
      (host.hasAttribute('open') || host.getAttribute('is-open') === 'true')
    );
  }

  // Keep the reveal in sync with the widget's open state: reveal it only while
  // it is actually open (so the default closed launcher never flashes), and drop
  // the reveal + FAB-hide once it closes.
  function syncReveal(host) {
    var root = document.documentElement;
    if (isChatOpen(host)) {
      root.classList.add('inbox-reveal');
    } else {
      root.classList.remove('inbox-reveal', 'chat-open');
    }
  }

  function watchState(host) {
    if (!host || closeObserver) return;
    closeObserver = new MutationObserver(function () {
      syncReveal(host);
    });
    closeObserver.observe(host, {
      attributes: true,
      attributeFilter: ['open', 'is-open', 'view']
    });
  }

  // Hide the FAB and open the chat panel. The widget is revealed only once it is
  // actually open (see syncReveal) so the default closed launcher never shows.
  function openChat() {
    var host = getHost();
    document.documentElement.classList.add('chat-open');
    watchState(host);

    var toggle = getToggle(host);
    if (toggle && typeof toggle.click === 'function') {
      toggle.click();
    } else if (host) {
      host.setAttribute('open', '');
    } else {
      console.warn('[after] Shopify Inbox chat launcher not found.');
      document.documentElement.classList.remove('chat-open');
      return;
    }
    // In case it opened synchronously (or was already open), reveal immediately.
    syncReveal(host);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-open-inbox]');
    if (!trigger) return;
    event.preventDefault();
    // Open on the next tick, after this click has finished propagating.
    // Opening synchronously lets Inbox's own "click outside closes" handler
    // see this same click and immediately close the panel we just opened.
    setTimeout(openChat, 0);
  });

  // Email-return flow (paired with theme.liquid + custom.css): reveal the Inbox
  // widget from first paint so Inbox can auto-open the conversation. Inbox only
  // auto-opens on a genuine email click-through; if it hasn't opened shortly
  // after the widget is ready (reload / direct hit), retract and show the FAB.
  function initEmailReturn() {
    if (!document.documentElement.classList.contains('inbox-email-return')) {
      return;
    }

    var fab = document.querySelector('.after-chat-fab');
    var seenOpen = false;
    var attempts = 0;
    var noOpenTimer = null;

    function revertToFab() {
      document.documentElement.classList.remove('inbox-email-return');
      if (fab) fab.classList.add('is-visible');
    }

    var poll = setInterval(function () {
      var host = getHost();

      if (!host) {
        if (++attempts > 80) clearInterval(poll); // ~24s
        return;
      }

      clearInterval(poll);

      noOpenTimer = setTimeout(function () {
        if (!seenOpen) {
          revertToFab();
          if (obs) obs.disconnect();
        }
      }, 1200);

      function check() {
        if (isChatOpen(host)) {
          seenOpen = true;
          if (noOpenTimer) {
            clearTimeout(noOpenTimer);
            noOpenTimer = null;
          }
          return;
        }
        // Revert only after it has actually been opened (the close), not the
        // initial closed state.
        if (seenOpen) {
          revertToFab();
          if (obs) obs.disconnect();
        }
      }

      var obs = new MutationObserver(check);
      obs.observe(host, {
        attributes: true,
        attributeFilter: ['open', 'is-open', 'view']
      });
      check();
    }, 300);
  }

  initEmailReturn();

  // Mirror Shopify Inbox's own "unread" indicator (it lives in the launcher's
  // shadow DOM) onto our custom FAB as a red dot, so a reply that arrives while
  // the tab is open but the chat is closed is visible even though the default
  // launcher is hidden.
  function hasUnreadIndicator(root) {
    if (!root) return false;

    var nodes = root.querySelectorAll(
      '[class*="unread"], [class*="Unread"], [class*="badge"], [class*="Badge"], [class*="notification"], [class*="Notification"]'
    );

    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getClientRects().length === 0) continue;
      var style = window.getComputedStyle(el);
      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        parseFloat(style.opacity) > 0
      ) {
        return true;
      }
    }

    return false;
  }

  function initUnreadWatcher() {
    var fab = document.querySelector('.after-chat-fab');
    if (!fab) return;

    var attempts = 0;
    var poll = setInterval(function () {
      var host = getHost();
      var root = host && host.shadowRoot;

      if (!root) {
        if (++attempts > 80) clearInterval(poll); // ~24s
        return;
      }

      clearInterval(poll);

      function sync() {
        var unread = hasUnreadIndicator(root);
        fab.classList.toggle('has-unread', unread);
        // Make sure the visitor actually sees the dot even before scrolling.
        if (unread) fab.classList.add('is-visible');
      }

      var unreadObserver = new MutationObserver(sync);
      unreadObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true
      });

      sync();
    }, 300);
  }

  initUnreadWatcher();

  // Debug helper: run `window.__afterInboxDump()` in the console to capture the
  // launcher's shadow DOM so the unread selector can be tightened if needed.
  window.__afterInboxDump = function () {
    var host = getHost();
    if (host && host.shadowRoot) {
      console.log(host.shadowRoot.innerHTML);
    } else {
      console.log('[after] Inbox shadow root not found yet.');
    }
  };

  // Hidden on load; reveal the FAB once the visitor has scrolled ~one screenful.
  (function () {
    var fab = document.querySelector('.after-chat-fab');
    if (!fab) return;

    function reveal() {
      if (window.scrollY > window.innerHeight * 0.4) {
        fab.classList.add('is-visible');
        window.removeEventListener('scroll', reveal);
      }
    }

    window.addEventListener('scroll', reveal, { passive: true });
    reveal(); // handle reloads that restore a scrolled position
  })();
})();
